import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { initialsOf } from '@/components/console/Avatar';
import { useT } from '@/i18n/labels';
import { useAgentStore } from '@/stores/agentStore';
import { useAuthStore } from '@/stores/authStore';
import type { Message } from '@/types';

/**
 * Stable hue (0-359) for a seed string. The same seed always yields the same hue, so one customer keeps one
 * avatar colour for the whole life of the conversation (and across reloads). Deliberately a pure string hash:
 * nothing random, nothing time-based, no per-render state.
 */
export function hueOf(seed: string | undefined | null): number {
  let h = 0;
  for (const ch of seed ?? '') h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/** An avatar background: a fixed saturation/lightness so every hue stays readable behind white initials. */
export function avatarColor(seed: string | undefined | null): string {
  // Comma syntax on purpose: it is the form every engine (and jsdom) parses; the space form is not universal.
  return `hsl(${hueOf(seed)}, 55%, 42%)`;
}

/**
 * The round profile picture beside every message in the merged timeline.
 *
 *  - CUSTOMER: the customer's initials on a colour seeded by the SESSION id (stable per conversation).
 *  - AI: the bot icon.
 *  - AGENT: the agent's initials, read from the tenant's agent directory. An agent who is not in the directory
 *    (deleted, or the directory fetch failed) gets a neutral person icon - NEVER the initials of a raw id.
 *
 * The ring says which conversation the row belongs to: red for the bot's internal consultation note, purple for a
 * staff member's guidance to the bot, green for the customer-facing chat. The customer and the bot's own chat messages carry no ring (the bubble colour already
 * says who spoke); only staff replies and consultation rows do.
 *
 * Decorative: the sender is also named in the bubble's label, so the avatar is `aria-hidden`.
 */
export const MsgAvatar: React.FC<{
  m: Message;
  /** The customer's display name (the PERSON, not the thread label). */
  customerName: string;
  /** Seeds the customer's colour. */
  sessionId: string;
}> = ({ m, customerName, sessionId }) => {
  const { t } = useT();
  const directory = useAgentStore((s) => s.directory);
  const me = useAuthStore((s) => s.user);

  const consult = m.stream === 'consult';

  if (m.sender === 'CUSTOMER') {
    return (
      <span
        className="msg-av customer"
        style={{ background: avatarColor(sessionId) }}
        data-hue={hueOf(sessionId)}
        title={customerName || undefined}
        aria-hidden="true"
        data-testid="msg-avatar"
      >
        {initialsOf(customerName)}
      </span>
    );
  }

  if (m.sender === 'AI') {
    return (
      <span
        className={'msg-av bot' + (consult ? ' ring-consult' : '')}
        title={t('sender_bot')}
        aria-hidden="true"
        data-testid="msg-avatar"
      >
        <Icon name="bot" size={16} />
      </span>
    );
  }

  // AGENT. 'me' is the sentinel the optimistic local guidance row carries for the signed-in user.
  const senderId = (m.senderId ?? '').trim();
  const isMe = senderId === 'me' || (!!me?.agentId && senderId === me.agentId);
  const lookupId = isMe ? me?.agentId ?? '' : senderId;
  const name = ((lookupId && directory[lookupId]) || (isMe ? me?.displayName : '') || '').trim();
  // 2026-10-28: an agent row in the consultation is GUIDANCE to the bot - purple, never the bot note's red.
  const ring = consult ? 'ring-guidance' : 'ring-customer';

  if (!name) {
    return (
      <span
        className={`msg-av agent-unknown ${ring}`}
        title={t('sender_agent_unknown')}
        aria-hidden="true"
        data-testid="msg-avatar"
      >
        <Icon name="user" size={16} />
      </span>
    );
  }

  return (
    <span
      className={`msg-av agent ${ring}`}
      style={{ background: avatarColor(lookupId) }}
      data-hue={hueOf(lookupId)}
      title={name}
      aria-hidden="true"
      data-testid="msg-avatar"
    >
      {initialsOf(name)}
    </span>
  );
};
